/**
 * The ExpandableText class represents a text container that can be expanded and collapsed.
 *
 * @class ExpandableText
 * @constructor Initializes the ExpandableText class with the provided text container.
 */
import { breakpoints, keyCodes, scrollToComponent } from '@msc-b2c/shared-utils';

export class ExpandableText {
  private readonly textContainer: HTMLElement;
  private btnMoreWrapper!: HTMLElement;
  private btnLessWrapper!: HTMLElement;
  private tables: NodeListOf<HTMLElement>;
  private dynamicNavTab: HTMLElement | null;

  private isValid = true;

  constructor(_textContainer: HTMLElement) {
    this.textContainer = _textContainer;
    const _btnMoreWrapper: HTMLElement | null | undefined =
      this.textContainer.querySelector('.cmp-text__button--more')?.parentElement;
    const _btnLessWrapper: HTMLElement | null | undefined =
      this.textContainer.querySelector('.cmp-text__button--less')?.parentElement;
    this.tables = this.textContainer.querySelectorAll('table');
    this.dynamicNavTab = document.querySelector('.dynamicnavigationtabs');

    if (!_btnMoreWrapper || !_btnLessWrapper) {
      this.isValid = false;
      return;
    }
    this.btnMoreWrapper = _btnMoreWrapper;
    this.btnLessWrapper = _btnLessWrapper;
  }

  /**
   * Appends a <span> element with specified classes to the element.
   *
   * @param element - The HTML element whose parent will receive the appended <span>.
   * @param classList - An array of class names to be added to the newly created <span>.
   */
  private appendSpanWithClasses(element: HTMLElement, classList: string[]): void {
    // case that this element already has an icon
    if (element.querySelector('.cmp-button__icon')) return;

    const span = document.createElement('span');

    span.classList.add(...classList);

    element.appendChild(span);
  }

  /**
   * Toggles the expanded state of the text container.
   * @param expand - Boolean indicating whether to expand or collapse.
   */
  private toggleText(expand: boolean): void {
    this.textContainer.classList.toggle('-expanded-text', expand);

    // ensure that there is no jumb because of change of height
    if (this.textContainer.classList.contains('cmp-parallax__paragraph-secondary')) {
      if (window.innerWidth < 768) return;
      const video = this.textContainer.closest('.cmp-parallax')?.querySelector('video');
      expand ? video?.pause() : video?.play();
      return;
    }

    if (!expand) {
      if (window.innerWidth <= breakpoints.lg) {
        const target =
          (this.btnLessWrapper.closest('.cmp-teaser__content') as HTMLElement | null) ??
          this.textContainer;

        this.scrollToTarget(target);
      } else {
        if (document.querySelector('.parallax')) return;
        this.btnMoreWrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  private getScrollParent(element: HTMLElement): HTMLElement {
    let parent = element.parentElement;

    while (parent) {
      const style = window.getComputedStyle(parent);
      const isScrollable =
        /(auto|scroll|overlay)/.test(style.overflowY) && parent.scrollHeight > parent.clientHeight;

      if (isScrollable) {
        return parent;
      }

      parent = parent.parentElement;
    }

    return document.scrollingElement as HTMLElement;
  }

  private scrollToTarget(target: HTMLElement): void {
    let offset = 0;
    requestAnimationFrame(() => {
      const scrollParent = this.getScrollParent(target);

      if (scrollParent === document.scrollingElement) {
        scrollToComponent(target);
        return;
      }

      // when scrolling up also take account of the navbar + some small clearence
      if (this.dynamicNavTab) {
        const clearenceOffset = 10;
        offset = this.dynamicNavTab.getBoundingClientRect().height + clearenceOffset;
      }

      const parentRect = scrollParent.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const top = scrollParent.scrollTop + (targetRect.top - parentRect.top) - offset;

      scrollParent.scrollTo({
        top,
        behavior: 'smooth',
      });
    });
  }

  private makeElementNavigatable(element: HTMLElement): void {
    element.setAttribute('tabindex', '0');
  }

  init() {
    // Prevent execution if the element is not valid
    if (!this.isValid) return;
    const isInParallax = this.textContainer.classList.contains('cmp-parallax__paragraph-secondary');

    // Ensure tables inside hidden text remain hidden
    Array.from(this.tables).forEach((table) => {
      if (table.querySelector('.cmp-text__hidden-text')) {
        table.classList.add('cmp-text__hidden-text');
      }
    });
    if (!isInParallax) {
      this.appendSpanWithClasses(this.btnMoreWrapper, ['cmp-button__icon', 'icon-down']);
      this.appendSpanWithClasses(this.btnLessWrapper, ['cmp-button__icon', 'icon-up']);
    }

    this.makeElementNavigatable(this.btnMoreWrapper);
    this.makeElementNavigatable(this.btnLessWrapper);

    this.btnMoreWrapper.addEventListener('click', () => this.toggleText(true));
    this.btnMoreWrapper.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === keyCodes.ENTER || event.key === keyCodes.SPACE) {
        event.preventDefault();
        this.toggleText(true);
        this.btnLessWrapper.focus();
      }
    });
    this.btnLessWrapper.addEventListener('click', () => this.toggleText(false));
    this.btnLessWrapper.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === keyCodes.ENTER || event.key === keyCodes.SPACE) {
        event.preventDefault();
        this.toggleText(false);
        this.btnMoreWrapper.focus();
      }
    });
  }
}

function onDocumentReady(): void {
  document.querySelectorAll('[class*="cmp-text__expandable"]').forEach((node) => {
    const expandableText = new ExpandableText(node as HTMLElement);
    expandableText.init();
  });

  document.querySelectorAll('.cmp-text__expandable--new').forEach((node) => {
    const btnMore = node.querySelector('p:has(>.cmp-text__button--more)');
    const btnLess = node.querySelector('p:has(>.cmp-text__button--less)');
    const contentContainer = node.querySelector('.cmp-text')!;

    if (btnLess && btnMore) {
      node.appendChild(btnMore);
      node.appendChild(btnLess);

      requestAnimationFrame(() => {
        const height = Math.ceil(contentContainer.getBoundingClientRect().height);
        const scrollHeight = contentContainer.scrollHeight;

        if (scrollHeight <= height) {
          btnLess.remove();
          btnMore.remove();
          node.classList.add('-expanded-text');
        }
      });
    }
  });
}

if (document.readyState !== 'loading') {
  onDocumentReady();
} else {
  document.addEventListener('DOMContentLoaded', onDocumentReady);
}
